import Plotly from 'plotly.js-dist-min';
import { useEffect, useRef, type MutableRefObject } from 'react';

export interface Figure {
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  frames: Plotly.Frame[] | null;
}

export interface PlotParams {
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  frames?: Plotly.Frame[] | undefined;
  config?: Partial<Plotly.Config> | undefined;
  /**
   * Callback executed after plot is initialized.
   * @param figure Object with three keys corresponding to input props: data, layout and frames.
   * @param graphDiv Reference to the DOM node into which the figure was rendered.
   */
  onInitialized?:
    | ((figure: Readonly<Figure>, graphDiv: Readonly<HTMLElement>) => void)
    | undefined;
  /**
   * Callback executed when when a plot is updated due to new data or layout, or when user interacts with a plot.
   * @param figure Object with three keys corresponding to input props: data, layout and frames.
   * @param graphDiv Reference to the DOM node into which the figure was rendered.
   */
  onUpdate?:
    | ((figure: Readonly<Figure>, graphDiv: Readonly<HTMLElement>) => void)
    | undefined;
  /**
   * Callback executed when component unmounts, before Plotly.purge strips the graphDiv of all private attributes.
   * @param figure Object with three keys corresponding to input props: data, layout and frames.
   * @param graphDiv Reference to the DOM node into which the figure was rendered.
   */
  onPurge?:
    | ((figure: Readonly<Figure>, graphDiv: Readonly<HTMLElement>) => void)
    | undefined;
  /**
   * Callback executed when a plotly.js API method rejects
   * @param err Error
   */
  onError?: ((err: Readonly<Error>) => void) | undefined;
  /**
   * id assigned to the <div> into which the plot is rendered.
   */
  divId?: string | undefined;
  /**
   * applied to the <div> into which the plot is rendered
   */
  className?: string | undefined;
  /**
   * used to style the <div> into which the plot is rendered
   */
  style?: React.CSSProperties | undefined;
  /**
   * When true, observes the plot container with ResizeObserver and calls
   * Plotly.Plots.resize() when its size changes.
   */
  useResizeHandler?: boolean | undefined;

  onAfterExport?: (() => void) | undefined;
  onAfterPlot?: (() => void) | undefined;
  onAnimated?: (() => void) | undefined;
  onAnimatingFrame?:
    | ((event: Readonly<Plotly.FrameAnimationEvent>) => void)
    | undefined;
  onAnimationInterrupted?: (() => void) | undefined;
  onAutoSize?: (() => void) | undefined;
  onBeforeExport?: (() => void) | undefined;
  onBeforeHover?:
    | ((event: Readonly<Plotly.PlotMouseEvent>) => boolean)
    | undefined;
  onButtonClicked?:
    | ((event: Readonly<Plotly.ButtonClickEvent>) => void)
    | undefined;
  onClick?: ((event: Readonly<Plotly.PlotMouseEvent>) => void) | undefined;
  onClickAnnotation?:
    | ((event: Readonly<Plotly.ClickAnnotationEvent>) => void)
    | undefined;
  onDeselect?: (() => void) | undefined;
  onDoubleClick?: (() => void) | undefined;
  onFramework?: (() => void) | undefined;
  onHover?: ((event: Readonly<Plotly.PlotHoverEvent>) => void) | undefined;
  onLegendClick?:
    | ((event: Readonly<Plotly.LegendClickEvent>) => boolean)
    | undefined;
  onLegendDoubleClick?:
    | ((event: Readonly<Plotly.LegendClickEvent>) => boolean)
    | undefined;
  onRelayout?:
    | ((event: Readonly<Plotly.PlotRelayoutEvent>) => void)
    | undefined;
  onRestyle?: ((event: Readonly<Plotly.PlotRestyleEvent>) => void) | undefined;
  onRedraw?: (() => void) | undefined;
  onSelected?:
    | ((event: Readonly<Plotly.PlotSelectionEvent>) => void)
    | undefined;
  onSelecting?:
    | ((event: Readonly<Plotly.PlotSelectionEvent>) => void)
    | undefined;
  onSliderChange?:
    | ((event: Readonly<Plotly.SliderChangeEvent>) => void)
    | undefined;
  onSliderEnd?: ((event: Readonly<Plotly.SliderEndEvent>) => void) | undefined;
  onSliderStart?:
    | ((event: Readonly<Plotly.SliderStartEvent>) => void)
    | undefined;
  onTransitioning?: (() => void) | undefined;
  onTransitionInterrupted?: (() => void) | undefined;
  onUnhover?: ((event: Readonly<Plotly.PlotMouseEvent>) => void) | undefined;
}

// -- type definitions --

type PlotlyGraphDiv = HTMLElement & {
  on: (event: string, handler: (...args: unknown[]) => unknown) => void;
  removeListener: (
    event: string,
    handler: (...args: unknown[]) => unknown
  ) => void;
};

type PlotEventBinding = readonly [string, (...args: unknown[]) => unknown];

type PlotParamsRef = MutableRefObject<PlotParams>;

// -- helper functions --

/**
 * Builds stable Plotly event bindings that always delegate to the latest props.
 *
 * @param propsRef - Ref containing the latest {@link PlotParams}.
 * @returns Event name/handler pairs for Plotly graph div registration.
 */
function buildPlotEventBindings(propsRef: PlotParamsRef): PlotEventBinding[] {
  return [
    [
      'plotly_afterexport',
      () => {
        propsRef.current.onAfterExport?.();
      },
    ],
    [
      'plotly_afterplot',
      () => {
        propsRef.current.onAfterPlot?.();
      },
    ],
    [
      'plotly_animated',
      () => {
        propsRef.current.onAnimated?.();
      },
    ],
    [
      'plotly_animatingframe',
      (event) => {
        propsRef.current.onAnimatingFrame?.(
          event as Plotly.FrameAnimationEvent
        );
      },
    ],
    [
      'plotly_animationinterrupted',
      () => {
        propsRef.current.onAnimationInterrupted?.();
      },
    ],
    [
      'plotly_autosize',
      () => {
        propsRef.current.onAutoSize?.();
      },
    ],
    [
      'plotly_beforeexport',
      () => {
        propsRef.current.onBeforeExport?.();
      },
    ],
    [
      'plotly_click',
      (event) => {
        propsRef.current.onClick?.(event as Plotly.PlotMouseEvent);
      },
    ],
    [
      'plotly_clickannotation',
      (event) => {
        propsRef.current.onClickAnnotation?.(
          event as Plotly.ClickAnnotationEvent
        );
      },
    ],
    [
      'plotly_deselect',
      () => {
        propsRef.current.onDeselect?.();
      },
    ],
    [
      'plotly_doubleclick',
      () => {
        propsRef.current.onDoubleClick?.();
      },
    ],
    [
      'plotly_framework',
      () => {
        propsRef.current.onFramework?.();
      },
    ],
    [
      'plotly_hover',
      (event) => {
        propsRef.current.onHover?.(event as Plotly.PlotHoverEvent);
      },
    ],
    [
      'plotly_legendclick',
      (event) => {
        return (
          propsRef.current.onLegendClick?.(event as Plotly.LegendClickEvent) ??
          true
        );
      },
    ],
    [
      'plotly_legenddoubleclick',
      (event) => {
        return (
          propsRef.current.onLegendDoubleClick?.(
            event as Plotly.LegendClickEvent
          ) ?? true
        );
      },
    ],
    [
      'plotly_redraw',
      () => {
        propsRef.current.onRedraw?.();
      },
    ],
    [
      'plotly_relayout',
      (event) => {
        propsRef.current.onRelayout?.(event as Plotly.PlotRelayoutEvent);
      },
    ],
    [
      'plotly_restyle',
      (event) => {
        propsRef.current.onRestyle?.(event as Plotly.PlotRestyleEvent);
      },
    ],
    [
      'plotly_selected',
      (event) => {
        propsRef.current.onSelected?.(event as Plotly.PlotSelectionEvent);
      },
    ],
    [
      'plotly_selecting',
      (event) => {
        propsRef.current.onSelecting?.(event as Plotly.PlotSelectionEvent);
      },
    ],
    [
      'plotly_sliderchange',
      (event) => {
        propsRef.current.onSliderChange?.(event as Plotly.SliderChangeEvent);
      },
    ],
    [
      'plotly_sliderend',
      (event) => {
        propsRef.current.onSliderEnd?.(event as Plotly.SliderEndEvent);
      },
    ],
    [
      'plotly_sliderstart',
      (event) => {
        propsRef.current.onSliderStart?.(event as Plotly.SliderStartEvent);
      },
    ],
    [
      'plotly_transitioning',
      () => {
        propsRef.current.onTransitioning?.();
      },
    ],
    [
      'plotly_transitioninterrupted',
      () => {
        propsRef.current.onTransitionInterrupted?.();
      },
    ],
    [
      'plotly_unhover',
      (event) => {
        propsRef.current.onUnhover?.(event as Plotly.PlotMouseEvent);
      },
    ],
  ];
}

/**
 * Registers Plotly graph div listeners and returns a detach function.
 *
 * @param graphDiv - Plotly graph div element returned by {@link Plotly.react}.
 * @param bindings - Event name/handler pairs to attach.
 * @returns Function that removes every registered listener.
 */
function attachPlotEventBindings(
  graphDiv: PlotlyGraphDiv,
  bindings: readonly PlotEventBinding[]
): () => void {
  bindings.forEach(([event, handler]) => {
    graphDiv.on(event, handler);
  });

  return () => {
    bindings.forEach(([event, handler]) => {
      graphDiv.removeListener(event, handler);
    });
  };
}

const Plot = (props: PlotParams): JSX.Element => {
  const divRef = useRef<HTMLDivElement | null>(null);
  const propsRef = useRef(props);
  propsRef.current = props;

  const plotEventBindingsRef = useRef<PlotEventBinding[]>(
    buildPlotEventBindings(propsRef)
  );
  const detachPlotEventBindingsRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    const ref = divRef.current;
    return () => {
      detachPlotEventBindingsRef.current?.();
      detachPlotEventBindingsRef.current = null;

      if (!ref) {
        return;
      }
      Plotly.purge(ref);
    };
  }, []);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ref = divRef.current!;
    let cancelled = false;

    Plotly.react(ref, props.data, props.layout, props.config)
      .then((graphDiv) => {
        if (cancelled) {
          return;
        }

        detachPlotEventBindingsRef.current?.();
        detachPlotEventBindingsRef.current = attachPlotEventBindings(
          graphDiv as unknown as PlotlyGraphDiv,
          plotEventBindingsRef.current
        );

        propsRef.current.onInitialized?.(graphDiv as unknown as Figure, ref);
      })
      .catch((err) => {
        if (!cancelled) {
          propsRef.current.onError?.(err as Error);
        }
      });

    return () => {
      cancelled = true;
      detachPlotEventBindingsRef.current?.();
      detachPlotEventBindingsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.config, props.data, props.layout]);

  useEffect(() => {
    if (!props.useResizeHandler) {
      return;
    }

    const el = divRef.current;
    if (!el) {
      return;
    }

    if (
      typeof ResizeObserver === 'undefined' ||
      process.env.NODE_ENV === 'test'
    ) {
      return;
    }

    const resize = () => {
      if (el.isConnected) {
        Plotly.Plots.resize(el);
      }
    };

    const target = el.parentElement ?? el;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(resize);
    });
    observer.observe(target);

    return () => observer.disconnect();
  }, [props.useResizeHandler]);

  return (
    <div
      id={props.divId}
      className={props.className}
      style={props.style}
      ref={divRef}
    ></div>
  );
};

export default Plot;
